import React, { useContext, useEffect, useState } from 'react';
import { Accordion, AccordionCollapse, AccordionContext, Form, useAccordionToggle } from 'react-bootstrap';
import styled, {keyframes} from 'styled-components';
import { t } from 'ttag';
import useOrderContext from '../../../../../contexts/order/useOrderContext';
import {rgba} from "polished";
import {Theme} from "../../../../../contexts/operator/operator-context";
import {money} from "../../../../../services/format";
import useBasketContext from "../../../../../contexts/basket/useBasketContext";
import useOperatorContext from "../../../../../contexts/operator/useOperatorContext";
import { CTA } from '../../../../general/Button';

interface TipAddBasketProps {
    id: string;
    label?: string;
    tipValue: number;
    disabled: boolean;
    defaultChecked?: boolean;
    selected: boolean;
    isConfirmationPage: boolean;
}

const TipAddBasket = ({
    id,
    label = t`Tip`,
    tipValue,
    disabled = false,
    defaultChecked = false,
    selected,
    isConfirmationPage = false,
}: TipAddBasketProps) => {

    const operatorContext = useOperatorContext();
    const orderContext = useOrderContext();
    const basketContext = useBasketContext();

    const [customTip, setCustomTip] = useState('');
    const [rawCustomTip, setRawCustomTip] = useState('');
    const [tipIndex, setTipIndex] = useState(0);

    const gratuityDefaultOptions = [
        0,
        (basketContext.validatedBasket?.subtotal ?? 0) * 0.1,
        (basketContext.validatedBasket?.subtotal ?? 0) * 0.15,
        (basketContext.validatedBasket?.subtotal ?? 0) * 0.2,
    ];

    const selectTip = (index: number) => {
        setTipIndex(index);
        setCustomTip('');
        setRawCustomTip('');

        if ( index > 0 ) {
            orderContext.setGratuitySelected(true);
            orderContext.setGratuityVal(Math.round(gratuityDefaultOptions[index]));
        } else {
            orderContext.setGratuitySelected(false);
            orderContext.setGratuityVal(0);
        }
    }

    const addCustomTip = () => {
        let num = parseFloat(rawCustomTip);

        if (isNaN(num)) {
            setTipIndex(0);
            setCustomTip('');
            setRawCustomTip('');
            orderContext.setGratuitySelected(false);
            orderContext.setGratuityVal(0);
        } else {
            let cleanNum = num.toFixed(2);
            setCustomTip(cleanNum);
            setRawCustomTip(cleanNum);
            orderContext.setGratuitySelected(true);
            orderContext.setGratuityVal(Math.round(num * 100));
        }
    }

    function CustomToggle({ children, eventKey, callback }: any) {
        const currentEventKey = useContext(AccordionContext);

        const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));

        const isCurrentEventKey = currentEventKey === eventKey;

        return (
            <button
                type="button"
                className={`customToggle ${isCurrentEventKey ? 'show' : ''}`}
                onClick={decoratedOnClick}
            >
                {children}
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </button>
        );
    }

    return (
        <>
            <Form.Group controlId={id} className={'mb-0'}>
                <Form.Label className={`d-block mb-0`}>
                    <Layout cursorNormal={ isConfirmationPage || tipValue === 0 || disabled }>
                        <span className="checkbox-label">{label}</span>
                        <span className="checkbox-price">{money(tipValue, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                    </Layout>
                </Form.Label>
            </Form.Group>
            <StyledTipSelector>
                <StyledTipButton className="tip-button" selected={tipIndex === 0 && customTip === ''} onClick={() => selectTip(0)}>{t`No tip`}</StyledTipButton>
                <StyledTipButton className="tip-button" selected={tipIndex === 1 && customTip === ''} onClick={() => selectTip(1)}>10%</StyledTipButton>
                <StyledTipButton className="tip-button" selected={tipIndex === 2 && customTip === ''} onClick={() => selectTip(2)}>15%</StyledTipButton>
                <StyledTipButton className="tip-button" selected={tipIndex === 3 && customTip === ''} onClick={() => selectTip(3)}>20%</StyledTipButton>
            </StyledTipSelector>
            <StyledAccordion defaultActiveKey="-1">
                <CustomToggle eventKey="0">
                    <div className="toggle">
                        <span className="toggle__title">{t`Add custom tip`}</span>
                    </div>
                </CustomToggle>
                <AccordionCollapse eventKey="0" timeout={2000}>
                    <StyledGroup controlId="formCheckoutTip" className="tip">
                        <Form.Control
                            placeholder={t`Enter custom amount`}
                            className=""
                            type="number"
                            value={rawCustomTip}
                            disabled={orderContext.loading}
                            onChange={(e: any) => setRawCustomTip(e.target.value)}>
                        </Form.Control>
                        <div className={'custom-tip__currency'}>£</div>

                        {rawCustomTip.length > 0 ? (
                            <CTA
                                className="submit-tip-btn"
                                onClick={() => addCustomTip()}
                                block
                            >
                                {t`Apply`}
                            </CTA>
                        ) : null }
                    </StyledGroup>
                </AccordionCollapse>
            </StyledAccordion>
        </>
    )
};

const StyledTipButton = styled.button<{selected: boolean}>`
    position: relative;
    flex-grow: 1;
    padding: 8px 16px;
    border: var(--ui_nav_channels_border);
    border-color: transparent;
    border-radius: var(--radius_m);
    background-color: var(--bg-grey);
    color: var(--ui_nav_channels_pill_inactive_label);
    transition: background-color 0.2s ease-in-out;
    margin-right: 8px;
    font-family: var(--text_heading_font);
    letter-spacing: var(--text_heading_font_spacing);
    text-transform: var(--text_heading_font_case);
    font-size: 16px;
    line-height: 16px;
    white-space: nowrap;

    ${(props) => props.selected ? `
        z-index: 5;
        background-color: var(--ui_nav_channels);
        color: var(--ui_nav_channels_pill_label);
        border: var(--ui_nav_channels_border);
    ` : `
        &:hover, &:focus {
            border: var(--ui_nav_channels_border);
        }

        &:hover {
            background-color: var(--bg-grey);
        }

        &:focus {
            outline: none;
        }
    `}
`;

const StyledTipSelector = styled.div`
    display: flex;
    margin-bottom: 12px;
`;

const StyledAccordion = styled(Accordion)`
    padding-bottom: 12px;

    .customToggle {
        transition: background-color 0.15s ease-in-out;

        .material-symbols-outlined {
            transition: transform 0.15s ease-in-out;
            font-size: 24px;
            line-height: 24px;
        }

        &.show {
            .material-symbols-outlined {
                transform: rotate(180deg);
            }
        }
    }

    > button {
        width: 100%;
        background: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        display: flex;
        padding: 0;
        margin: 0;
        font-weight: 600;
        align-items: center;
        text-align: left;
        color: var(--text_body_color);

        img {
            transition: transform 0.2s linear;
        }

        &.show {
            img {
                transform: rotate(180deg);
            }
        }

        .toggle {
            display: flex;
            color: var(--text_body_color);
            flex-wrap: wrap;
            flex-grow: 1;
            > span {
                margin-right: 8px;
            }
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
        }
    }
`;

const StyledGroup = styled(Form.Group)`
    margin: 8px 0 12px 0;
    position: relative;

    .form-control {
        padding-left: calc(24px + 12px);
    }

    .custom-tip__currency {
        position: absolute;
        top: 0;
        left: 0;
        height: 48px;
        width: 24px;
        line-height: 48px;
        font-size: 16px;
        text-align: right;
        font-weight: 500;
    }

    button {
        width: auto;
    }

    .submit-tip-btn {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        background-color: transparent;
        color: var(--text-black);
        height: auto;
        padding: 4px;

        &:disabled {
            background: var(--bg-disabled);
            color: white;
        }
    }
`;

const Layout = styled.div<{ cursorNormal: boolean }>`
    display: flex;
    align-items: center;
    cursor: ${(props) => props.cursorNormal ? 'normal' : 'pointer' };
    margin-bottom: 12px;
    .checkbox-label {
        flex: 1;
    }

    .checkbox-label,
    .checkbox-price {
        font-size: 16px;
        line-height: 24px;
        color: var(--text-black);
    }
`;


export default TipAddBasket;
